import classNames from "classnames";
import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../Button";
import "./expandable-text.scss";

export type TExpandableText = {
    text: string;
    renderText?: (arg0: string) => ReactNode;
    collapsedMaxLength?: number;
    collapsedButtonText: string;
    expandedButtonText: string;
    inlineButton?: boolean;
    textClassName?: string;
    buttonClassName?: string;
    collapsedLineClamp?: number;
};

export const ExpandableText = ({
    text,
    renderText,
    collapsedMaxLength = 500,
    collapsedButtonText,
    expandedButtonText,
    inlineButton = false,
    textClassName,
    buttonClassName,
    collapsedLineClamp,
}: TExpandableText) => {
    const [expanded, setExpanded] = useState(false);
    const textRef = useRef<HTMLParagraphElement | null>(null);
    const textIsShort = text.length <= collapsedMaxLength;
    const isTextClamped = () => textRef.current && textRef.current.scrollHeight > textRef.current.clientHeight;
    const [textIsClamped, setTextIsClamped] = useState(isTextClamped());
    const textId = useId();

    const showButton = expanded || textIsClamped || !textIsShort;
    // Return full text if line-clamp is used, if the the text is shorter then the max-length, or if the text is expanded.
    const calculatedText =
        collapsedLineClamp || textIsShort || expanded ? `${text} ` : `${text.substring(0, collapsedMaxLength)}... `;

    useEffect(() => {
        setTextIsClamped(isTextClamped());
    }, [expanded, text, collapsedLineClamp]);

    return (
        <div className="c-expandable-text">
            <div
                id={textId}
                ref={textRef}
                style={{ WebkitLineClamp: collapsedLineClamp }}
                className={classNames("c-expandable-text__text", textClassName, {
                    "c-expandable-text__text--inline": inlineButton,
                    "c-expandable-text__text--w-line-clamp": collapsedLineClamp && !expanded,
                    "c-expandable-text__text--no-btn": !showButton,
                })}
                tabIndex={showButton ? 0 : undefined}
                role="textbox"
                aria-readonly="true"
                aria-multiline="true"
            >
                {renderText?.(calculatedText) ?? calculatedText}
            </div>
            {showButton && (
                <Button
                    type="tertiary"
                    onClick={() => setExpanded(prevState => !prevState)}
                    buttonClassName={buttonClassName}
                    aria-controls={textId}
                    aria-expanded={expanded}
                >
                    {expanded ? expandedButtonText : collapsedButtonText}
                </Button>
            )}
        </div>
    );
};
